import {
  Grid,
  Stack,
  debounce,
  useMediaQuery,
  useTheme,
  TextField as MuiTextField,
} from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { App } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SelectField from "~/components/Extras/SelectField";
import TextField from "~/components/Extras/TextField";
import GenericForm from "~/components/GenericForm";
import GenericFormContainer from "~/components/GenericFormContainer";
import MDBox from "~/components/MDBox";
import MDButton from "~/components/MDButton";
import { roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import {
  getGeoLocations,
  useLocationMutation,
  useUpdateLocation,
} from "~/queries/locationMasterQuery";
import { getPlantCategory } from "~/services/plant.service";
import { updateInitialValues, updateValidationSchema } from "./helper";
import GoogleAutocomplete from "~/utils/components/GoogleAutocomplete";
import MyLocationMap from "~/components/MyLocationMap";

const CreateLocation = () => {
  const theme = useTheme();
  const childRef = useRef();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [altitude, setAltitude] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [address, setAddress] = useState("");
  const [manuallySetLocation, setManuallySetLocation] = useState(false);
  const [submitButtonLabel, setSubmitButtonLabel] = useState("Submit");
  const [cardHeaderTitle, setCardHeaderTitle] = useState("Create Location");
  const formRef = useRef(null);

  const { message: antMessage } = App.useApp();
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const createLocationMutation = useLocationMutation();
  const updateLocationMutation = useUpdateLocation();
  const geoLocation = getGeoLocations();
  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

  const categories = useQueries({
    queries: [
      {
        queryKey: ["Land_Slope", 0],
        queryFn: () => getPlantCategory("Land Slope"),
      },
      {
        queryKey: ["Land_Type", 1],
        queryFn: () => getPlantCategory("Land Type"),
      },
    ],
  });

  const handleSubmit = async (values, helpers) => {
    const datalocation = [latitude, longitude];
    try {
      const data = await geoLocation.mutateAsync(datalocation);
      if (data && data.results.length > 0) {
        setAltitude(data.results[0].elevation);
      } else {
        setAltitude(null);
      }
    } catch (error) {
      console.log(error);
    }
    if (location.state && location.pathname.includes("location-master/update")) {
      try {
        const updateResponse = await updateLocationMutation.mutateAsync({
          ...values,
          params: location.state?.Loc_Auto_ID,
          Latitude: latitude,
          Longitude: longitude,
          Altitude_meter: altitude,
          Location: address,
        });
        antMessage.success(updateResponse.data.message);
        navigate("/location-master");
      } catch (error) {
        antMessage.error(error?.response?.data?.message);
      }
    } else {
      try {
        const createResponse = await createLocationMutation.mutateAsync({
          ...values,
          Latitude: latitude,
          Longitude: longitude,
          Altitude_meter: altitude,
          Location: address,
        });
        antMessage.success(createResponse?.message);
        navigate("/location-master");
      } catch (error) {
        antMessage.error(error?.response?.data?.message);
      }
    }
  };

  const handleChange = (info) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-2);
    newFileList = newFileList.map((file) => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });
    formRef.current.setFieldValue("Plant_Img", newFileList[0].originFileObj);
    setFileList(newFileList);
    if (info.file.status === "removed") {
      setFileList([]);
    }
  };

  const fetchLocationName = useCallback(async (latitude, longitude) => {
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.status === "OK" && data.results.length > 0) {
      const locationName = data.results[0].formatted_address;
      return locationName;
    } else {
      throw new Error("No location found");
    }
  }, [GOOGLE_MAPS_API_KEY]);

  const fetchLocation = useCallback(
    async (latitude, longitude) => {
      try {
        const locationName = await fetchLocationName(latitude, longitude);

        return locationName;
      } catch (error) {
        console.error("Error fetching location name:", error);
      }
    },
    [fetchLocationName]
  );

  const handleLatitudeChange = useCallback(
    async (event) => {
      event.preventDefault();
      const newLatitude = event.target.value;
      setLatitude(newLatitude);
      const locationName = await fetchLocation(newLatitude, longitude);
      setAddress(locationName); // Update address state
    },
    [fetchLocation, longitude]
  );

  const handleLongitudeChange = useCallback(
    async (event) => {
      event.preventDefault();
      const newLongitude = event.target.value;
      setLongitude(newLongitude);
      const locationName = await fetchLocation(latitude, newLongitude);
      setAddress(locationName); // Update address state
    },
    [fetchLocation, latitude]
  );

  useEffect(() => {
    if (location.state && location.pathname.includes("location-master/update")) {
      setSubmitButtonLabel("Update");
      setCardHeaderTitle("Update Location");

      const { setFieldValue } = formRef.current;
      for (const key in location.state) {
        if (Object.hasOwnProperty.call(location.state, key)) {
          const value = location.state[key];
          setFieldValue(key, value);
        }
      }
      if (!location.state.Latitude && !location.state.Longitude && !location.state.Location) {
        // Fetch system latitude and longitude
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude: sysLatitude, longitude: sysLongitude } = position.coords;
            setLatitude(sysLatitude);
            setLongitude(sysLongitude);

            // Fetch location name based on system latitude and longitude
            try {
              const locationName = await fetchLocationName(sysLatitude, sysLongitude);
              setAddress(locationName);
            } catch (error) {
              console.error("Error fetching location name:", error);
            }
          },
          (error) => {
            console.error("Error getting system location:", error);
          }
        );
      } else {
        setLatitude(location.state.Latitude);
        setLongitude(location.state.Longitude);
        setAddress(location.state.Location);
      }
    } else {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: sysLatitude, longitude: sysLongitude } = position.coords;
          setLatitude(sysLatitude);
          setLongitude(sysLongitude);

          // Fetch location name based on system latitude and longitude
          try {
            const locationName = await fetchLocationName(sysLatitude, sysLongitude);
            setAddress(locationName);
          } catch (error) {
            console.error("Error fetching location name:", error);
          }
        },
        (error) => {
          console.error("Error getting system location:", error);
        }
      );
    }
  }, [location, fetchLocationName]);

  function getGoogleAutocomplete(place) {
    if (place !== null) {
      const { structured_formatting, description, geometry } = place;
      const { lat, lng } = geometry;
      formRef.current.setFieldValue("Latitude", lat);
      formRef.current.setFieldValue("Longitude", lng);
      setLatitude(lat);
      setLongitude(lng);
      setAddress(description);
      setManuallySetLocation(true);
    }
  }

  return (
    <GenericFormContainer
      cardHeaderTitle={cardHeaderTitle}
      navigateTo={location.state === true ? -1 : "/location-master"}
      p={2}
    >
      <Grid container spacing={2}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <MDBox p={2}>
            <GenericForm
              initialValues={updateInitialValues}
              validationSchema={updateValidationSchema}
              onSubmit={handleSubmit}
              validateOnChange={handleChange}
              innerRef={formRef}
              loadingStatus={
                createLocationMutation.isLoading || updateLocationMutation.isLoading
              }
              loadingText={
                createLocationMutation.isLoading ? "Creating..." : "Updating..."
              }
            >
              <Grid container spacing={2}>
                <Grid item lg={3} md={3} sm={12} xs={12}>
                  <TextField
                    label="Location Name"
                    name="Loc_Name"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={12} xs={12}>
                  <TextField
                    type="number"
                    label="Total Location area (Hect.)"
                    name="Loc_Total_Area"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={12} xs={12}>
                  <SelectField
                    label="Location Type"
                    name="Loc_Type"
                    options={categories[0].data?.data?.map((item) => ({
                      value: item.Cat_Value,
                      label: item.Cat_Value,
                    }))}
                    placeholder="Select Options"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={12} xs={12}>
                  <SelectField
                    name="Type_of_land"
                    label="Type of Land"
                    placeholder="Select Options"
                    options={categories[1]?.data?.data?.map((item) => ({
                      value: item.Cat_Value,
                      label: item.Cat_Value,
                    }))}
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={12} xs={12}>
                  <TextField
                    type="number"
                    label="Total Plotting Count"
                    name="Total_Ploting_Count"
                    isRequired={true}
                  />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <GoogleAutocomplete
                    ref={childRef}
                    getPlaceOption={getGoogleAutocomplete}
                    locationValue={address ? address : "Add a location"}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={12} xs={12}>
                  <MuiTextField
                    label="Latitude"
                    name="Latitude"
                    value={latitude || ""}
                    onChange={handleLatitudeChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={12} xs={12}>
                  <MuiTextField
                    label="Longitude"
                    name="Longitude"
                    value={longitude || ""}
                    onChange={handleLongitudeChange}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid
                  my={2}
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: matchesXs ? "flex-end" : "end",
                  }}
                >
                  <Stack
                    direction={matchesXs ? "column-reverse" : "row"}
                    spacing={2}
                    useFlexGap
                  >
                    <MDButton
                      sx={{ px: 5 }}
                      variant="contained"
                      color="error"
                      type="reset"
                      size={matchesXs ? "small" : "large"}
                      onClick={() => {
                        if (
                          location.state &&
                          location.pathname.includes("location-master/update")
                        ) {
                          navigate(-1);
                        } else {
                          formRef.current?.handleReset();
                        }
                      }}
                    >
                      Cancel
                    </MDButton>
                    {userHasRequiredRole && (
                      <MDButton
                        sx={{ px: 6 }}
                        type="submit"
                        disabled={!userHasRequiredRole}
                        fullWidth
                        variant="contained"
                        color="success"
                        size={matchesXs ? "small" : "large"}
                      >
                        {submitButtonLabel}
                      </MDButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </GenericForm>
          </MDBox>
        </Grid>
        <Grid item lg={12} md={12} sm={6} xs={6}>
          <MDBox p={2}>
            <MyLocationMap
              coordinates={{ lat: +latitude, lng: +longitude }}
              location={address ? address : "Location Name"}
            />
          </MDBox>
        </Grid>
      </Grid>
    </GenericFormContainer>
  );
};

export default CreateLocation;
